import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { Fullscreen, Minimize, VolumeX, Volume2 } from "lucide-react";
import { SectionHeading } from "components/misc/Headings";

// Layout styles
const VideoContent = styled.div`
  ${tw`relative w-full overflow-hidden h-96`}
  @media (min-width: 640px) {
    height: 400px;
    aspect-ratio: 16 / 9;
  }
`;

const VideoBackground = styled.video`
  ${tw`w-full h-full object-cover`}
`;

const VideoOverlay = tw.div`absolute top-0 right-0 mt-2 mr-2 flex items-center gap-2 z-10`;
const OverlayButton = tw.button`p-2 rounded-full bg-black/50 hover:bg-black text-white transition`;
const Subheading = tw(SectionHeading)`mb-4 text-center text-2xl font-bold`;

const HeadingInfo = ({ subheading, ...props }) => (
  <div {...props}>
    {subheading ? <Subheading>{subheading}</Subheading> : null}
  </div>
);

const VideoWrapper = ({ videoUrl }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const scrollRef = useRef(0);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  };

  const toggleFullScreen = () => {
    if (videoRef.current) {
      // Save scroll position before going fullscreen
      scrollRef.current = window.scrollY;

      if (!document.fullscreenElement) {
        videoRef.current.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    }
  };

  // Restore scroll position when fullscreen exits
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      setFullscreen(isFullscreen);

      if (!isFullscreen) {
        window.scrollTo({ top: scrollRef.current, behavior: "instant" });
      }
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  const handlePlayClick = () => {
    if (videoRef.current?.paused) {
      videoRef.current.play();
    } else {
      videoRef.current?.pause();
    }
  };

  return (
    <>
      <HeadingInfo
        tw="text-center lg:block"
        subheading={"Unforgettable Destination - Fall in Love with the Journey"}
      />

      <VideoContent>
        <VideoOverlay>
          <OverlayButton onClick={toggleMute} title={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </OverlayButton>
          <OverlayButton onClick={toggleFullScreen} title="Fullscreen">
            {fullscreen ? <Minimize size={20} /> : <Fullscreen size={20} />}
          </OverlayButton>
        </VideoOverlay>

        <VideoBackground
          ref={videoRef}
          src={videoUrl}
          autoPlay
          loop
          muted={muted}
          playsInline
          onClick={handlePlayClick}
        />
      </VideoContent>
    </>
  );
};

export default VideoWrapper;
